import React from 'react'
import InputActionButton from '@/components/InputActionButton'
import type { SDUIComponentProps } from '@/types/sduiTypes'

/**
 * SDUI ActionButton adapter — wraps the InputActionButton compound control
 * (a text input paired with a trailing action button) to conform to the SDUI
 * component contract.
 *
 * - The input is data-bound: its value comes from the data model and edits flow
 *   back through `onChange`.
 * - The trailing button triggers the component's `onClick` action and shows a
 *   spinner while that action is in flight (`isLoading`).
 *
 * Maps to the backend `ActionButton` component type via the ComponentRegistry.
 */
const SDUIActionButton: React.FC<SDUIComponentProps> = ({
    id,
    label,
    value,
    errors,
    isLoading,
    disabled,
    placeholder,
    type,
    actionIcon,
    actionLabel,
    onChange,
    onClick,
    onBlur,
}) => {
    const hasErrors = errors && errors.length > 0

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(e.target.value)
    }

    const handleInputBlur = () => {
        onBlur?.()
    }

    const handleAction = () => {
        onClick?.()
    }

    return (
        <div className="w-full">
            {label && (
                <label
                    htmlFor={id}
                    className="block font-medium text-gray-700 dark:text-gray-200 mb-1 text-sm"
                >
                    {label}
                </label>
            )}
            <InputActionButton
                id={id}
                type={(type as string) || 'text'}
                value={(value as string) ?? ''}
                disabled={disabled}
                invalid={hasErrors}
                placeholder={(placeholder as string) || ''}
                autoComplete="off"
                actionIcon={(actionIcon as React.ReactNode) ?? '⋯'}
                actionLabel={actionLabel as React.ReactNode}
                actionLoading={isLoading}
                actionAriaLabel={label || 'Action'}
                onChange={handleChange}
                onInputBlur={handleInputBlur}
                onAction={handleAction}
            />
            {hasErrors && (
                <div className="mt-1">
                    {errors.map((err, idx) => (
                        <p
                            key={`${id}-error-${idx}`}
                            className="text-red-500 text-xs"
                        >
                            {err}
                        </p>
                    ))}
                </div>
            )}
        </div>
    )
}

SDUIActionButton.displayName = 'SDUIActionButton'

export default SDUIActionButton
